/**
 * Unified Notification Service
 * Combines OneSignal + Native Web Push with fallbacks and retries
 */

import { supabase } from '@/integrations/supabase/client';

// ============= TYPES =============

export interface NotificationStatus {
  isSupported: boolean;
  permission: NotificationPermission;
  isIOS: boolean;
  isIOSSupportsPush: boolean;
  isInstalledPWA: boolean;
  hasNativeSubscription: boolean;
  hasDbSubscription: boolean;
  isFullyEnabled: boolean;
}

export interface NotificationResult {
  success: boolean;
  method: 'onesignal' | 'native' | 'both' | 'none';
  error?: string;
}

// ============= CONSTANTS =============

const VAPID_PUBLIC_KEY = 'BP90LFmRD9u3bb-DGAEvfsXqWThDl9jgZIdaTOnB259IJVHRFuJ2UEVLFODdF_q5j1H5PDcH5yzdt60O9WFagnA';
const MAX_RETRIES = 3;
const RETRY_DELAY = 2000;

// ============= PLATFORM DETECTION =============

export function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

export function isInstalledPWA(): boolean {
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  const isIOSStandalone = (navigator as { standalone?: boolean }).standalone === true;
  return isStandalone || isIOSStandalone;
}

export function isIOSSupportsPush(): boolean {
  if (!isIOS()) return true;
  
  const match = navigator.userAgent.match(/OS (\d+)_(\d+)/);
  if (match) {
    const major = parseInt(match[1], 10);
    const minor = parseInt(match[2], 10);
    if (major < 16 || (major === 16 && minor < 4)) {
      return false;
    }
  }
  return 'PushManager' in window;
}

export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 
         'PushManager' in window && 
         'Notification' in window;
}

export function getPermission(): NotificationPermission {
  if (!('Notification' in window)) return 'denied';
  return Notification.permission;
}

// ============= HELPERS =============

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

async function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// ============= NATIVE WEB PUSH =============

async function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  
  try {
    return await navigator.serviceWorker.ready;
  } catch (error) {
    console.error('[NotifService] SW registration error:', error);
    return null;
  }
}

async function getNativeSubscription(): Promise<PushSubscription | null> {
  try {
    const registration = await navigator.serviceWorker.ready;
    return await registration.pushManager.getSubscription();
  } catch {
    return null;
  }
}

async function createNativeSubscription(): Promise<PushSubscription | null> {
  console.log('[NotifService] Creating native subscription...');
  
  const registration = await getServiceWorkerRegistration();
  if (!registration) {
    console.error('[NotifService] No SW registration');
    return null;
  }

  try {
    const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey.buffer as ArrayBuffer
    });
    
    console.log('[NotifService] Native subscription created');
    return subscription;
  } catch (error) {
    console.error('[NotifService] Failed to create subscription:', error);
    return null;
  }
}

async function saveSubscriptionToDb(userId: string, subscription: PushSubscription): Promise<boolean> {
  const subscriptionJSON = subscription.toJSON();
  const keys = subscriptionJSON.keys;
  
  if (!keys?.p256dh || !keys?.auth) {
    console.error('[NotifService] Missing subscription keys');
    return false;
  }

  console.log('[NotifService] Saving subscription to DB (upsert by endpoint)...');

  // UPSERT: Update if endpoint exists for this user, insert otherwise
  // This allows multiple devices per user (different endpoints)
  const { error } = await supabase
    .from('push_subscriptions')
    .upsert({
      user_id: userId,
      endpoint: subscription.endpoint,
      p256dh: keys.p256dh,
      auth: keys.auth,
      updated_at: new Date().toISOString()
    }, {
      onConflict: 'user_id,endpoint'
    });

  if (error) {
    console.error('[NotifService] DB save error:', error);
    return false;
  }

  console.log('[NotifService] Subscription saved to DB');
  return true;
}

async function removeSubscriptionFromDb(userId: string, endpoint?: string): Promise<void> {
  if (endpoint) {
    // Remove only the subscription for this specific device
    console.log('[NotifService] Removing subscription for endpoint:', endpoint.substring(0, 50) + '...');
    await supabase
      .from('push_subscriptions')
      .delete()
      .eq('user_id', userId)
      .eq('endpoint', endpoint);
  } else {
    // Fallback: remove all subscriptions for this user
    console.log('[NotifService] Removing all subscriptions for user:', userId);
    await supabase
      .from('push_subscriptions')
      .delete()
      .eq('user_id', userId);
  }
}

// ============= MAIN API =============

/**
 * Request notification permission from browser
 */
export async function requestPermission(): Promise<boolean> {
  if (!('Notification' in window)) {
    console.warn('[NotifService] Notifications not supported');
    return false;
  }

  try {
    console.log('[NotifService] Requesting permission...');
    const result = await Notification.requestPermission();
    console.log('[NotifService] Permission result:', result);
    return result === 'granted';
  } catch (error) {
    console.error('[NotifService] Permission error:', error);
    return false;
  }
}

/**
 * Subscribe user to push notifications (with retries)
 * Tries native Web Push + stores in DB for server-side sending
 */
export async function subscribe(userId: string, retryCount = 0): Promise<boolean> {
  console.log('[NotifService] Subscribe attempt', retryCount + 1, 'for user:', userId);

  // Validate environment
  if (!isPushSupported()) {
    console.warn('[NotifService] Push not supported');
    return false;
  }

  if (isIOS()) {
    if (!isIOSSupportsPush()) {
      console.warn('[NotifService] iOS version too old');
      return false;
    }
    if (!isInstalledPWA()) {
      console.warn('[NotifService] iOS requires PWA installation');
      return false;
    }
  }

  try {
    // Get or create native subscription
    let subscription = await getNativeSubscription();
    
    if (!subscription) {
      subscription = await createNativeSubscription();
    }

    if (!subscription) {
      if (retryCount < MAX_RETRIES) {
        console.log('[NotifService] Retrying in', RETRY_DELAY, 'ms...');
        await delay(RETRY_DELAY);
        return subscribe(userId, retryCount + 1);
      }
      return false;
    }

    // Save to database for server-side push
    const saved = await saveSubscriptionToDb(userId, subscription);
    
    if (!saved && retryCount < MAX_RETRIES) {
      console.log('[NotifService] DB save failed, retrying...');
      await delay(RETRY_DELAY);
      return subscribe(userId, retryCount + 1);
    }

    // Update profile
    await supabase
      .from('profiles')
      .update({ push_notifications_enabled: true })
      .eq('id', userId);

    console.log('[NotifService] ✅ Subscription complete');
    return saved;
  } catch (error) {
    console.error('[NotifService] Subscribe error:', error);
    
    if (retryCount < MAX_RETRIES) {
      await delay(RETRY_DELAY);
      return subscribe(userId, retryCount + 1);
    }
    return false;
  }
}

/**
 * Unsubscribe user from push notifications (current device only)
 */
export async function unsubscribe(userId: string): Promise<boolean> {
  console.log('[NotifService] Unsubscribing current device for user:', userId);

  try {
    // Get current device subscription
    const subscription = await getNativeSubscription();
    const currentEndpoint = subscription?.endpoint;
    
    // Unsubscribe from native push
    if (subscription) {
      await subscription.unsubscribe();
      console.log('[NotifService] Native subscription removed');
    }

    // Remove only this device's subscription from database
    await removeSubscriptionFromDb(userId, currentEndpoint);

    // Check if user still has other device subscriptions
    const { data: remainingSubscriptions } = await supabase
      .from('push_subscriptions')
      .select('id')
      .eq('user_id', userId);

    // Only disable in profile if no subscriptions remain
    if (!remainingSubscriptions || remainingSubscriptions.length === 0) {
      console.log('[NotifService] No remaining subscriptions, disabling in profile');
      await supabase
        .from('profiles')
        .update({ push_notifications_enabled: false })
        .eq('id', userId);
    } else {
      console.log('[NotifService] User still has', remainingSubscriptions.length, 'other device(s) subscribed');
    }

    console.log('[NotifService] ✅ Unsubscribe complete');
    return true;
  } catch (error) {
    console.error('[NotifService] Unsubscribe error:', error);
    return false;
  }
}

/**
 * Send a push notification to a user (via Edge Function)
 * The Edge Function will try OneSignal first, then fall back to VAPID
 */
export async function sendNotification(
  userId: string,
  title: string,
  body: string,
  data?: Record<string, unknown>
): Promise<NotificationResult> {
  console.log('[NotifService] Sending notification to:', userId);

  try {
    const { data: result, error } = await supabase.functions.invoke('send-push-notification', {
      body: { userId, title, body, data }
    });

    if (error) {
      console.error('[NotifService] Edge function error:', error);
      return { success: false, method: 'none', error: error.message };
    }

    console.log('[NotifService] Notification result:', result);
    return {
      success: result?.success || false,
      method: result?.method || 'native',
      error: result?.error
    };
  } catch (error) {
    console.error('[NotifService] Send error:', error);
    return { success: false, method: 'none', error: String(error) };
  }
}

/**
 * Show a local notification (immediate, no server)
 */
export async function showLocalNotification(
  title: string,
  body: string,
  options?: { tag?: string; data?: Record<string, unknown> }
): Promise<boolean> {
  if (Notification.permission !== 'granted') {
    console.warn('[NotifService] Permission not granted');
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    
    await registration.showNotification(title, {
      body,
      icon: '/pwa-192x192.png',
      badge: '/pwa-192x192.png',
      tag: options?.tag || `local-${Date.now()}`,
      data: options?.data,
    });
    
    return true;
  } catch (error) {
    // Fallback to Notification API
    try {
      new Notification(title, { body, icon: '/pwa-192x192.png' });
      return true;
    } catch {
      return false;
    }
  }
}

/**
 * Get full notification status
 */
export async function getStatus(userId?: string): Promise<NotificationStatus> {
  const supported = isPushSupported();
  const permission = getPermission();
  const ios = isIOS();
  const iosSupportsPush = isIOSSupportsPush();
  const installedPWA = isInstalledPWA();

  let hasNativeSubscription = false;
  let hasDbSubscription = false;

  if (supported) {
    const subscription = await getNativeSubscription();
    hasNativeSubscription = !!subscription;
  }

  if (userId) {
    // Check if user has at least one subscription (any device)
    const { data } = await supabase
      .from('push_subscriptions')
      .select('id')
      .eq('user_id', userId)
      .limit(1);
    hasDbSubscription = !!(data && data.length > 0);
  }

  const isFullyEnabled = permission === 'granted' && hasDbSubscription;

  return {
    isSupported: supported,
    permission,
    isIOS: ios,
    isIOSSupportsPush: iosSupportsPush,
    isInstalledPWA: installedPWA,
    hasNativeSubscription,
    hasDbSubscription,
    isFullyEnabled
  };
}

/**
 * Auto-sync: If browser has subscription but DB doesn't, sync it
 */
export async function autoSync(userId: string): Promise<boolean> {
  const status = await getStatus(userId);
  
  if (status.hasNativeSubscription && !status.hasDbSubscription) {
    console.log('[NotifService] Auto-syncing subscription to DB...');
    return await subscribe(userId);
  }
  
  return false;
}

/**
 * Listen for subscription changes from service worker
 */
export function listenForChanges(callback: () => void): () => void {
  const handler = (event: MessageEvent) => {
    if (event.data?.type === 'PUSH_SUBSCRIPTION_CHANGED') {
      console.log('[NotifService] Subscription changed event');
      callback();
    }
  };
  
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', handler);
  }
  
  return () => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.removeEventListener('message', handler);
    }
  };
}
